import React, { useState } from 'react';
import { Book, Code, Key, CreditCard, BarChart3, Copy, ChevronDown, ChevronRight, AlertCircle, CheckCircle } from 'lucide-react';

interface CodeExample {
  language: string;
  code: string;
}

interface Parameter {
  name: string;
  type: string;
  required: boolean;
  description: string;
  example?: string;
}

interface ApiEndpoint {
  method: string;
  path: string;
  title: string;
  description: string;
  auth: 'jwt' | 'api-key' | 'none';
  parameters?: Parameter[];
  requestBody?: {
    type: string;
    properties: Parameter[];
  };
  responses: {
    code: number;
    description: string;
    example: any;
  }[];
  examples: CodeExample[];
}

const APIReference: React.FC = () => {
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({
    authentication: true,
    getting_started: true,
  });
  const [copiedCode, setCopiedCode] = useState<string | null>(null);

  const toggleSection = (section: string) => {
    setOpenSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const copyCode = (code: string, id: string) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(id);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const apiEndpoints: Record<string, ApiEndpoint[]> = {
    authentication: [
      {
        method: 'POST',
        path: '/api/v1/auth/login',
        title: 'Portal Login',
        description: 'Authenticate to access the portal and manage API keys',
        auth: 'none',
        requestBody: {
          type: 'application/json',
          properties: [
            { name: 'email', type: 'string', required: true, description: 'User email address', example: 'user@fenxlabs.ai' },
            { name: 'password', type: 'string', required: true, description: 'User password', example: 'password123' }
          ]
        },
        responses: [
          {
            code: 200,
            description: 'Login successful',
            example: {
              success: true,
              data: {
                token: 'jwt_token_here',
                user: {
                  uid: 'user_id',
                  email: 'user@fenxlabs.ai',
                  creditBalance: 10.0
                }
              }
            }
          },
          {
            code: 401,
            description: 'Invalid credentials',
            example: {
              success: false,
              error: {
                code: 'INVALID_CREDENTIALS',
                message: 'Invalid email or password'
              }
            }
          }
        ],
        examples: [
          {
            language: 'curl',
            code: `curl -X POST https://api.askarc.app/api/v1/auth/login \\
  -H "Content-Type: application/json" \\
  -d '{
    "email": "user@fenxlabs.ai",
    "password": "password123"
  }'`
          },
          {
            language: 'javascript',
            code: `const response = await fetch('https://api.askarc.app/api/v1/auth/login', {
  method: 'POST',
  headers: {
    'Content-Type': 'application/json',
  },
  body: JSON.stringify({
    email: 'user@fenxlabs.ai',
    password: 'password123'
  })
});

const data = await response.json();
const token = data.data.token;`
          },
          {
            language: 'javascript',
            code: `// Document Analysis with OpenAI client (AskArc Extension)
import OpenAI from 'openai';

const client = new OpenAI({
  apiKey: 'fxk_your_api_key_here',
  baseURL: 'https://api.askarc.app/api/v1'
});

const completion = await client.chat.completions.create({
  model: 'gemini-1.5-pro',
  messages: [
    {
      role: 'user',
      content: [
        {
          type: 'text',
          text: 'Analyze this document'
        },
        {
          type: 'document',
          document: {
            data: 'JVBERi0xLjQKJdPr6eEKMSAwIG9iao...',
            mime_type: 'application/pdf',
            filename: 'analysis.pdf'
          }
        }
      ]
    }
  ],
  temperature: 0.7,
  max_tokens: 500
});

console.log(completion.choices[0].message.content);`
          },
          {
            language: 'python',
            code: `import requests

response = requests.post('https://api.askarc.app/api/v1/auth/login', 
    json={
        'email': 'user@fenxlabs.ai',
        'password': 'password123'
    }
)

data = response.json()
token = data['data']['token']`
          }
        ]
      }
    ],
    models: [
      {
        method: 'GET',
        path: '/api/v1/models',
        title: 'Get Available Models',
        description: 'Get list of all available AI models with their IDs and details',
        auth: 'api-key',
        responses: [
          {
            code: 200,
            description: 'Models retrieved successfully',
            example: [
              {
                id: 'model-identifier',
                name: 'Model Display Name',
                provider: 'openai',
                description: 'Brief model description',
                category: 'standard',
                contextWindow: 128000,
                maxTokens: 4096,
                supports_vision: true,
                supports_streaming: true,
                ranked_position: 1
              }
            ]
          }
        ],
        examples: [
          {
            language: 'curl',
            code: `curl -X GET https://api.askarc.app/api/v1/models \\
  -H "X-API-Key: fxk_your_api_key_here"`
          },
          {
            language: 'javascript',
            code: `const response = await fetch('https://api.askarc.app/api/v1/models', {
  headers: {
    'X-API-Key': 'fxk_your_api_key_here'
  }
});
const data = await response.json();
const models = data.data; // Array of available model objects`
          },
          {
            language: 'python',
            code: `import requests

response = requests.get('https://api.askarc.app/api/v1/models',
    headers={
        'X-API-Key': 'fxk_your_api_key_here'
    }
)
data = response.json()
models = data['data']  # List of available model objects`
          }
        ]
      }
    ],
    keys: [
      {
        method: 'POST',
        path: '/api/v1/portal/keys',
        title: 'Create API Key',
        description: 'Create a new API key for external usage',
        auth: 'jwt',
        requestBody: {
          type: 'application/json',
          properties: [
            { name: 'name', type: 'string', required: true, description: 'Name for the API key', example: 'My App API Key' }
          ]
        },
        responses: [
          {
            code: 201,
            description: 'API key created successfully',
            example: {
              success: true,
              data: {
                id: 'key_id_123',
                name: 'My App API Key',
                key: 'fxk_1234567890abcdef...',
                isActive: true,
                usageCount: 0,
                creditsUsed: 0,
                createdAt: '2025-08-18T10:00:00Z'
              }
            }
          }
        ],
        examples: [
          {
            language: 'curl',
            code: `curl -X POST https://api.askarc.app/api/v1/portal/keys \\
  -H "Authorization: Bearer YOUR_JWT_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{
    "name": "My App API Key"
  }'`
          },
          {
            language: 'javascript',
            code: `const response = await fetch('https://api.askarc.app/api/v1/portal/keys', {
  method: 'POST',
  headers: {
    'Authorization': 'Bearer ' + jwtToken,
    'Content-Type': 'application/json',
  },
  body: JSON.stringify({
    name: 'My App API Key'
  })
});

const data = await response.json();
const apiKey = data.data.key; // Save this securely!`
          }
        ]
      },
      {
        method: 'GET',
        path: '/api/v1/portal/keys',
        title: 'List API Keys',
        description: 'Get all API keys for the authenticated user',
        auth: 'jwt',
        responses: [
          {
            code: 200,
            description: 'API keys retrieved successfully',
            example: {
              success: true,
              data: [
                {
                  id: 'key_id_123',
                  name: 'My App API Key',
                  isActive: true,
                  usageCount: 150,
                  creditsUsed: 2.45,
                  lastUsedAt: '2025-08-18T09:30:00Z',
                  createdAt: '2025-08-15T10:00:00Z'
                }
              ]
            }
          }
        ],
        examples: [
          {
            language: 'curl',
            code: `curl -X GET https://api.askarc.app/api/v1/portal/keys \\
  -H "Authorization: Bearer YOUR_JWT_TOKEN"`
          }
        ]
      },
      {
        method: 'DELETE',
        path: '/api/v1/portal/keys/:keyId',
        title: 'Revoke API Key',
        description: 'Permanently revoke an API key',
        auth: 'jwt',
        parameters: [
          { name: 'keyId', type: 'string', required: true, description: 'ID of the API key to revoke', example: 'key_id_123' }
        ],
        responses: [
          {
            code: 200,
            description: 'API key revoked successfully',
            example: {
              success: true,
              data: {
                message: 'API key revoked successfully'
              }
            }
          }
        ],
        examples: [
          {
            language: 'curl',
            code: `curl -X DELETE https://api.askarc.app/api/v1/portal/keys/key_id_123 \\
  -H "Authorization: Bearer YOUR_JWT_TOKEN"`
          }
        ]
      }
    ],
    credits: [
      {
        method: 'GET',
        path: '/api/v1/portal/credits',
        title: 'Get Credit Balance',
        description: 'Get current credit balance and usage information',
        auth: 'jwt',
        responses: [
          {
            code: 200,
            description: 'Credit balance retrieved successfully',
            example: {
              success: true,
              data: {
                balance: 15.75,
                totalUsed: 4.25,
                lastActivity: '2025-08-18T09:30:00Z'
              }
            }
          }
        ],
        examples: [
          {
            language: 'curl',
            code: `curl -X GET https://api.askarc.app/api/v1/portal/credits \\
  -H "Authorization: Bearer YOUR_JWT_TOKEN"`
          }
        ]
      }
    ],
    openai_compatible: [
      {
        method: 'POST',
        path: '/api/v1/chat/completions',
        title: 'Chat Completions',
        description: 'OpenAI-compatible chat completions endpoint. Drop-in replacement for OpenAI API with the same request/response format.',
        auth: 'api-key',
        requestBody: {
          type: 'application/json',
          properties: [
            { name: 'model', type: 'string', required: false, description: 'Model ID to use. Omit or pass "auto" to let the system pick the best model for the task.', example: 'gemini-2.0-flash' },
            { name: 'messages', type: 'array', required: true, description: 'Array of conversation messages with role and content', example: '[{"role": "user", "content": "Hello!"}]' },
            { name: 'temperature', type: 'number', required: false, description: 'Sampling temperature (0-2)', example: '0.7' },
            { name: 'max_tokens', type: 'number', required: false, description: 'Maximum tokens to generate', example: '150' },
            { name: 'top_p', type: 'number', required: false, description: 'Nucleus sampling parameter (0-1)', example: '1.0' },
            { name: 'stream', type: 'boolean', required: false, description: 'Enable SSE streaming', example: 'false' },
            { name: 'tools', type: 'array', required: false, description: 'Tool definitions. Pass {"type":"web_search_preview"} to enable live web search with citations.', example: '[{"type":"web_search_preview"}]' }
          ]
        },
        responses: [
          {
            code: 200,
            description: 'Chat completion generated successfully',
            example: {
              id: 'chatcmpl-123',
              object: 'chat.completion',
              created: 1677652288,
              model: 'gpt-4o-mini',
              choices: [{
                index: 0,
                message: {
                  role: 'assistant',
                  content: 'Hello! How can I help you today?'
                },
                finish_reason: 'stop'
              }],
              usage: {
                prompt_tokens: 9,
                completion_tokens: 12,
                total_tokens: 21
              }
            }
          },
          {
            code: 401,
            description: 'Invalid API key',
            example: {
              error: {
                message: 'Invalid or inactive API key',
                type: 'authentication_error',
                code: 'invalid_api_key'
              }
            }
          },
          {
            code: 403,
            description: 'Insufficient credits',
            example: {
              error: {
                message: 'Insufficient credits for this request',
                type: 'invalid_request_error',
                code: 'insufficient_credits'
              }
            }
          }
        ],
        examples: [
          {
            language: 'curl',
            code: `curl -X POST https://api.askarc.app/api/v1/chat/completions \\
  -H "X-API-Key: fxk_your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "messages": [
      {"role": "system", "content": "You are helpful."},
      {"role": "user", "content": "Hello!"}
    ],
    "temperature": 0.7,
    "max_tokens": 150
  }'`
          },
          {
            language: 'curl',
            code: `# Image Analysis Example
curl -X POST https://api.askarc.app/api/v1/chat/completions \\
  -H "X-API-Key: fxk_your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "messages": [
      {
        "role": "user",
        "content": [
          {
            "type": "text",
            "text": "What do you see in this image?"
          },
          {
            "type": "image_url",
            "image_url": {
              "url": "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mP8/5+hHgAHggJ/PchI7wAAAABJRU5ErkJggg=="
            }
          }
        ]
      }
    ],
    "temperature": 0.7,
    "max_tokens": 300
  }'`
          },
          {
            language: 'curl',
            code: `# Web Search Example
curl -X POST https://api.askarc.app/api/v1/chat/completions \\
  -H "X-API-Key: fxk_your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "model": "gemini-2.0-flash",
    "messages": [
      {"role": "user", "content": "What AI companies launched products this week?"}
    ],
    "tools": [{"type": "web_search_preview"}]
  }'`
          },
          {
            language: 'curl',
            code: `# Document Analysis Example (AskArc Extension)
curl -X POST https://api.askarc.app/api/v1/chat/completions \\
  -H "X-API-Key: fxk_your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "messages": [
      {
        "role": "user",
        "content": [
          {
            "type": "text",
            "text": "Please analyze this document"
          },
          {
            "type": "document",
            "document": {
              "data": "JVBERi0xLjQKJdPr6eEKMSAwIG9iao...",
              "mime_type": "application/pdf",
              "filename": "report.pdf"
            }
          }
        ]
      }
    ],
    "temperature": 0.7,
    "max_tokens": 500
  }'`
          },
          {
            language: 'python',
            code: `# Using OpenAI Python library
import openai

client = openai.OpenAI(
    api_key="fxk_your_api_key_here",
    base_url="https://api.askarc.app/api/v1"
)

completion = client.chat.completions.create(
    messages=[
        {"role": "system", "content": "You are helpful."},
        {"role": "user", "content": "Hello!"}
    ],
    temperature=0.7,
    max_tokens=150
)

print(completion.choices[0].message.content)`
          },
          {
            language: 'python',
            code: `# Image Analysis with OpenAI Python library
import openai

client = openai.OpenAI(
    api_key="fxk_your_api_key_here",
    base_url="https://api.askarc.app/api/v1"
)

completion = client.chat.completions.create(
    messages=[
        {
            "role": "user",
            "content": [
                {
                    "type": "text",
                    "text": "What do you see in this image?"
                },
                {
                    "type": "image_url",
                    "image_url": {
                        "url": "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mP8/5+hHgAHggJ/PchI7wAAAABJRU5ErkJggg=="
                    }
                }
            ]
        }
    ],
    temperature=0.7,
    max_tokens=300
)

print(completion.choices[0].message.content)`
          }
        ]
      },
      {
        method: 'POST',
        path: '/api/v1/chat/completions',
        title: 'Chat Completions (Streaming)',
        description: 'Stream chat completions in real-time using Server-Sent Events (SSE). Set "stream": true to receive incremental response chunks as they are generated.',
        auth: 'api-key',
        requestBody: {
          type: 'application/json',
          properties: [
            { name: 'messages', type: 'array', required: true, description: 'Array of conversation messages with role and content', example: '[{"role": "user", "content": "Hello!"}]' },
            { name: 'stream', type: 'boolean', required: true, description: 'Must be set to true to enable streaming', example: 'true' },
            { name: 'temperature', type: 'number', required: false, description: 'Sampling temperature (0-2)', example: '0.7' },
            { name: 'max_tokens', type: 'number', required: false, description: 'Maximum tokens to generate', example: '150' },
            { name: 'top_p', type: 'number', required: false, description: 'Nucleus sampling parameter (0-1)', example: '1.0' }
          ]
        },
        responses: [
          {
            code: 200,
            description: 'SSE stream (Content-Type: text/event-stream). Each line is "data: {chunk}" ending with "data: [DONE]"',
            example: {
              'Content-Type': 'text/event-stream',
              '_note': 'Each chunk arrives as: data: {json}',
              chunk: {
                id: 'chatcmpl-123',
                object: 'chat.completion.chunk',
                created: 1677652288,
                model: 'gpt-4o-mini',
                choices: [{ delta: { content: 'Hello' }, finish_reason: null }]
              },
              '_terminator': 'data: [DONE]'
            }
          },
          {
            code: 401,
            description: 'Invalid API key',
            example: { error: { message: 'Invalid or inactive API key', type: 'authentication_error', code: 'invalid_api_key' } }
          }
        ],
        examples: [
          {
            language: 'curl',
            code: `curl -X POST https://api.askarc.app/api/v1/chat/completions \\
  -H "X-API-Key: fxk_your_api_key_here" \\
  -H "Content-Type: application/json" \\
  --no-buffer \\
  -d '{
    "messages": [
      {"role": "user", "content": "Tell me a short story."}
    ],
    "stream": true,
    "temperature": 0.7,
    "max_tokens": 300
  }'`
          },
          {
            language: 'javascript',
            code: `const response = await fetch('https://api.askarc.app/api/v1/chat/completions', {
  method: 'POST',
  headers: {
    'X-API-Key': 'fxk_your_api_key_here',
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({
    messages: [{ role: 'user', content: 'Tell me a short story.' }],
    stream: true,
    temperature: 0.7,
    max_tokens: 300
  })
});

const reader = response.body.getReader();
const decoder = new TextDecoder();

while (true) {
  const { done, value } = await reader.read();
  if (done) break;

  const text = decoder.decode(value);
  for (const line of text.split('\\n')) {
    if (line.startsWith('data: ')) {
      const data = line.slice(6);
      if (data === '[DONE]') break;
      const chunk = JSON.parse(data);
      const content = chunk.choices[0]?.delta?.content || '';
      process.stdout.write(content);
    }
  }
}`
          },
          {
            language: 'python',
            code: `import openai

client = openai.OpenAI(
    api_key="fxk_your_api_key_here",
    base_url="https://api.askarc.app/api/v1"
)

stream = client.chat.completions.create(
    messages=[{"role": "user", "content": "Tell me a short story."}],
    stream=True,
    temperature=0.7,
    max_tokens=300
)

for chunk in stream:
    content = chunk.choices[0].delta.content or ""
    print(content, end="", flush=True)`
          }
        ]
      }
    ]
  };

  const availableModels = [
    { id: 'gpt-4o', name: 'GPT-4o', description: 'Most capable model, best for complex tasks' },
    { id: 'gpt-4o-mini', name: 'GPT-4o Mini', description: 'Fast and cost-effective for most tasks' },
    { id: 'claude-3-5-sonnet', name: 'Claude 3.5 Sonnet', description: 'Excellent for analysis and writing' },
    { id: 'gemini-1.5-pro', name: 'Gemini 1.5 Pro', description: 'Great for documents and large context' }
  ];

  const renderAuthBadge = (auth: string) => {
    const badges = {
      'jwt': { cls: '', text: 'JWT Required' },
      'api-key': { cls: 'pk-badge-success', text: 'API Key Required' },
      'none': { cls: 'pk-badge-outline', text: 'No Auth' }
    };
    const badge = badges[auth as keyof typeof badges];
    return <span className={`pk-badge ${badge.cls}`}>{badge.text}</span>;
  };

  const renderMethod = (method: string) => {
    const cls = {
      'GET': 'pk-method-get',
      'POST': 'pk-method-post',
      'PUT': 'pk-badge-warning',
      'DELETE': 'pk-method-del'
    }[method] || '';
    return <span className={`pk-badge pk-method ${cls}`}>{method}</span>;
  };

  const SectionHeader: React.FC<{
    id: string;
    title: string;
    icon: React.ReactNode;
    description?: string;
  }> = ({ id, title, icon, description }) => (
    <div
      style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', padding: '16px 0', borderBottom: '1px solid var(--arc-line)' }}
      onClick={() => toggleSection(id)}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div className="pk-i-plate">{icon}</div>
        <div>
          <h2 className="pk-h3">{title}</h2>
          {description && <p className="pk-sub" style={{ marginTop: 2 }}>{description}</p>}
        </div>
      </div>
      {openSections[id] ? <ChevronDown size={18} className="pk-i" /> : <ChevronRight size={18} className="pk-i" />}
    </div>
  );

  return (
    <div style={{ maxWidth: 1080, margin: '0 auto' }}>
      <div style={{ marginBottom: 28 }}>
        <h1 className="pk-h1" style={{ marginBottom: 10 }}>API Reference</h1>
        <p className="pk-body">Complete documentation for the AskArc API. Use these endpoints to integrate AI capabilities into your applications.</p>
      </div>

      {/* Quick Start */}
      <div style={{ marginBottom: 28 }}>
        <SectionHeader
          id="getting_started"
          title="Quick Start"
          icon={<Book size={16} />}
          description="Get up and running with the AskArc API in minutes"
        />

        {openSections.getting_started && (
          <div style={{ padding: '24px 0', display: 'flex', flexDirection: 'column', gap: 20 }}>
            <div className="pk-card">
              <h3 className="pk-h4" style={{ marginBottom: 14 }}>Getting Started</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {[
                  { n: 1, title: 'Login to the Portal', desc: 'Access the developer portal to manage your API keys and credits' },
                  { n: 2, title: 'Create an API Key', desc: 'Generate a secure API key for your application' },
                  { n: 3, title: 'Start Making Requests', desc: 'Use your API key to send messages and get AI responses' },
                ].map(step => (
                  <div key={step.n} style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                    <span style={{
                      flexShrink: 0, width: 24, height: 24, borderRadius: '50%', background: 'var(--arc-accent)', color: '#fff',
                      display: 'grid', placeItems: 'center', fontSize: 12, fontWeight: 700,
                    }}>{step.n}</span>
                    <div>
                      <p style={{ fontWeight: 700, color: 'var(--arc-ink)', margin: 0 }}>{step.title}</p>
                      <p className="pk-small">{step.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="pk-card" style={{ background: 'var(--arc-bg-2)' }}>
              <h3 className="pk-h4" style={{ marginBottom: 10 }}>Base URL</h3>
              <div className="pk-secret"><span className="val">https://api.askarc.app</span></div>
            </div>

            <div className="pk-card">
              <h3 className="pk-h4" style={{ marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" style={{ color: 'var(--arc-warning)' }}>
                  <path d="M13.527.099C6.955-.744.942 3.9.099 10.473c-.843 6.572 3.8 12.584 10.373 13.428 6.573.843 12.587-3.801 13.428-10.374C24.744 6.955 20.101.943 13.527.099zM15.026 16.319c-.548 1.326-1.342 1.783-2.502 1.783-.904 0-1.932-.527-2.502-1.783-.546-1.2-.546-2.676 0-3.876.57-1.256 1.598-1.783 2.502-1.783 1.16 0 1.954.457 2.502 1.783.546 1.2.546 2.676 0 3.876zm-6.864-14.25c6.048-.84 11.62 3.42 12.46 9.468.84 6.048-3.42 11.62-9.468 12.46C5.106 24.837-.466 20.577-1.306 14.529c-.84-6.048 3.42-11.62 9.468-12.46z"/>
                </svg>
                Test with Postman
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <p className="pk-body">We've created a complete Postman collection to help you test all API endpoints easily.</p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                  <a href="https://www.postman.com/downloads/" target="_blank" rel="noopener noreferrer" className="pk-btn pk-btn-ink">
                    Download Postman
                  </a>
                  <button
                    type="button"
                    onClick={() => {
                      const collectionUrl = '/portal/AskArc_API_Keys.postman_collection.json';
                      window.open(collectionUrl, '_blank');
                    }}
                    className="pk-btn pk-btn-outline"
                  >
                    Download Collection
                  </button>
                </div>
                <div className="pk-card-inset">
                  <h4 style={{ fontWeight: 700, fontSize: 13.5, color: 'var(--arc-ink)', marginBottom: 6 }}>Quick Setup:</h4>
                  <ol className="pk-small" style={{ display: 'flex', flexDirection: 'column', gap: 3, paddingLeft: 16 }}>
                    <li>Download and install Postman</li>
                    <li>Import our collection using the download link above</li>
                    <li>Set the <code className="pk-mono">baseUrl</code> variable to your API endpoint</li>
                    <li>Login via the Portal Authentication folder to get your JWT token</li>
                    <li>Create API keys and start testing!</li>
                  </ol>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Authentication */}
      <div style={{ marginBottom: 28 }}>
        <SectionHeader
          id="authentication"
          title="Authentication"
          icon={<Key size={16} />}
          description="Two authentication methods for different use cases"
        />

        {openSections.authentication && (
          <div style={{ padding: '24px 0', display: 'flex', flexDirection: 'column', gap: 20 }}>
            <div className="pk-grid pk-grid-2">
              <div className="pk-card">
                <h3 className="pk-h4" style={{ marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Key size={16} style={{ color: 'var(--arc-accent)' }} />
                  API Key Authentication
                </h3>
                <p className="pk-small" style={{ marginBottom: 14 }}>For external applications making API calls</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <div>
                    <h4 style={{ fontWeight: 700, fontSize: 13, color: 'var(--arc-ink)', marginBottom: 4 }}>Header Format:</h4>
                    <div className="pk-secret"><span className="val">X-API-Key: fxk_your_api_key_here</span></div>
                  </div>
                  <div>
                    <h4 style={{ fontWeight: 700, fontSize: 13, color: 'var(--arc-ink)', marginBottom: 4 }}>Alternative Format:</h4>
                    <div className="pk-secret"><span className="val">Authorization: Bearer fxk_your_api_key_here</span></div>
                  </div>
                </div>
              </div>

              <div className="pk-card">
                <h3 className="pk-h4" style={{ marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <CheckCircle size={16} style={{ color: 'var(--arc-success)' }} />
                  JWT Authentication
                </h3>
                <p className="pk-small" style={{ marginBottom: 14 }}>For portal management operations</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <div>
                    <h4 style={{ fontWeight: 700, fontSize: 13, color: 'var(--arc-ink)', marginBottom: 4 }}>Header Format:</h4>
                    <div className="pk-secret"><span className="val">Authorization: Bearer your_jwt_token</span></div>
                  </div>
                  <div>
                    <h4 style={{ fontWeight: 700, fontSize: 13, color: 'var(--arc-ink)', marginBottom: 4 }}>Usage:</h4>
                    <p className="pk-small">Required for API key management, credit operations, and analytics</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Authentication endpoints */}
            {apiEndpoints.authentication && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                <h3 className="pk-h4">Portal Authentication</h3>
                {apiEndpoints.authentication.map((endpoint, index) => (
                  <EndpointCard key={index} endpoint={endpoint} copyCode={copyCode} copiedCode={copiedCode} />
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Models */}
      <div style={{ marginBottom: 28 }}>
        <SectionHeader
          id="models"
          title="Available Models"
          icon={<Code size={16} />}
          description="Get list of all available AI models"
        />

        {openSections.models && (
          <div style={{ padding: '24px 0', display: 'flex', flexDirection: 'column', gap: 20 }}>
            {apiEndpoints.models?.map((endpoint, index) => (
              <EndpointCard key={index} endpoint={endpoint} copyCode={copyCode} copiedCode={copiedCode} />
            ))}
          </div>
        )}
      </div>

      {/* API Key Management */}
      <div style={{ marginBottom: 28 }}>
        <SectionHeader
          id="api_keys"
          title="API Key Management"
          icon={<Key size={16} />}
          description="Create, list, and manage your API keys"
        />

        {openSections.api_keys && (
          <div style={{ padding: '24px 0', display: 'flex', flexDirection: 'column', gap: 20 }}>
            {apiEndpoints.keys?.map((endpoint, index) => (
              <EndpointCard key={index} endpoint={endpoint} copyCode={copyCode} copiedCode={copiedCode} />
            ))}
          </div>
        )}
      </div>

      {/* Credit Management */}
      <div style={{ marginBottom: 28 }}>
        <SectionHeader
          id="credits"
          title="Credit Management"
          icon={<CreditCard size={16} />}
          description="Monitor and manage your account credits"
        />

        {openSections.credits && (
          <div style={{ padding: '24px 0', display: 'flex', flexDirection: 'column', gap: 20 }}>
            {apiEndpoints.credits?.map((endpoint, index) => (
              <EndpointCard key={index} endpoint={endpoint} copyCode={copyCode} copiedCode={copiedCode} />
            ))}
          </div>
        )}
      </div>

      {/* Image Handling */}
      <div style={{ marginBottom: 28 }}>
        <SectionHeader
          id="image_handling"
          title="Image Handling"
          icon={<Code size={16} />}
          description="Send images using the standard OpenAI multimodal format"
        />

        {openSections.image_handling && (
          <div style={{ padding: '24px 0' }}>
            <div className="pk-card">
              <h3 className="pk-h4" style={{ marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
                <Code size={16} style={{ color: 'var(--arc-accent)' }} />
                OpenAI Compatible Format
              </h3>
              <p className="pk-small" style={{ marginBottom: 14 }}>Use multimodal content arrays in messages — the standard OpenAI format, fully supported by our endpoint.</p>
              <pre className="pk-codeblock">
{`{
  "messages": [
    {
      "role": "user",
      "content": [
        {
          "type": "text",
          "text": "Analyze this image"
        },
        {
          "type": "image_url",
          "image_url": {
            "url": "data:image/png;base64,..."
          }
        }
      ]
    }
  ]
}`}
              </pre>
            </div>
          </div>
        )}
      </div>

      {/* Chat Completions */}
      <div style={{ marginBottom: 28 }}>
        <SectionHeader
          id="messages"
          title="Chat Completions (OpenAI Compatible)"
          icon={<Code size={16} />}
          description="OpenAI-compatible endpoint for chat completions — standard and streaming"
        />

        {openSections.messages && (
          <div style={{ padding: '24px 0', display: 'flex', flexDirection: 'column', gap: 20 }}>
            {apiEndpoints.openai_compatible?.map((endpoint, index) => (
              <EndpointCard key={index} endpoint={endpoint} copyCode={copyCode} copiedCode={copiedCode} />
            ))}
          </div>
        )}
      </div>

      {/* Error Codes */}
      <div style={{ marginBottom: 28 }}>
        <SectionHeader
          id="errors"
          title="Error Codes"
          icon={<AlertCircle size={16} />}
          description="Common error responses and how to handle them"
        />

        {openSections.errors && (
          <div style={{ padding: '24px 0' }}>
            <div className="pk-card">
              <h3 className="pk-h4" style={{ marginBottom: 16 }}>Common Error Responses</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <ErrorCodeCard
                  code={401}
                  title="UNAUTHORIZED"
                  description="Invalid or missing authentication"
                  example={{
                    success: false,
                    error: {
                      code: 'UNAUTHORIZED',
                      message: 'Authentication required'
                    }
                  }}
                />
                <ErrorCodeCard
                  code={402}
                  title="INSUFFICIENT_CREDITS"
                  description="Not enough credits to complete the request"
                  example={{
                    success: false,
                    error: {
                      code: 'INSUFFICIENT_CREDITS',
                      message: 'Insufficient credits for this request',
                      details: {
                        required: 0.0025,
                        available: 0.001
                      }
                    }
                  }}
                />
                <ErrorCodeCard
                  code={404}
                  title="NOT_FOUND"
                  description="Resource not found"
                  example={{
                    success: false,
                    error: {
                      code: 'NOT_FOUND',
                      message: 'API key not found'
                    }
                  }}
                />
                <ErrorCodeCard
                  code={429}
                  title="RATE_LIMITED"
                  description="Too many requests"
                  example={{
                    success: false,
                    error: {
                      code: 'RATE_LIMITED',
                      message: 'Rate limit exceeded'
                    }
                  }}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Component for individual endpoint cards
const EndpointCard: React.FC<{
  endpoint: ApiEndpoint;
  copyCode: (code: string, id: string) => void;
  copiedCode: string | null;
}> = ({ endpoint, copyCode, copiedCode }) => {
  const [activeTab, setActiveTab] = useState(0);

  return (
    <div className="pk-card pk-card-flush">
      <div className="pk-card-head" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {renderMethod(endpoint.method)}
          <code className="pk-mono" style={{ fontSize: 13.5, color: 'var(--arc-ink)' }}>{endpoint.path}</code>
          {renderAuthBadge(endpoint.auth)}
        </div>
        <h3 className="pk-h4">{endpoint.title}</h3>
        <p className="pk-small">{endpoint.description}</p>
      </div>

      <div className="pk-card-body" style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        {/* Request Body */}
        {endpoint.requestBody && (
          <div>
            <h4 className="pk-h4" style={{ fontSize: 14, marginBottom: 10 }}>Request Body</h4>
            <div className="pk-card-inset" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {endpoint.requestBody.properties.map((param, index) => (
                <div key={index} style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                  <code className="pk-mono" style={{ fontSize: 12.5, background: 'var(--arc-paper)', border: '1px solid var(--arc-line)', borderRadius: 'var(--arc-r-xs)', padding: '2px 6px', flexShrink: 0 }}>{param.name}</code>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span className="pk-small">{param.type}</span>
                      {param.required && <span className="pk-badge pk-badge-danger">Required</span>}
                    </div>
                    <p className="pk-small" style={{ color: 'var(--arc-ink-2)', marginTop: 2 }}>{param.description}</p>
                    {param.example && (
                      <code className="pk-mono pk-small" style={{ display: 'block', marginTop: 2 }}>Example: {JSON.stringify(param.example)}</code>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Responses */}
        <div>
          <h4 className="pk-h4" style={{ fontSize: 14, marginBottom: 10 }}>Responses</h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {endpoint.responses.map((response, index) => (
              <div key={index} className="pk-card pk-card-flush">
                <div style={{ padding: '8px 14px', borderBottom: '1px solid var(--arc-line-2)', background: 'var(--arc-bg-2)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontWeight: 700, fontSize: 13, color: response.code === 200 || response.code === 201 ? 'var(--arc-success)' : 'var(--arc-danger)' }}>
                      {response.code}
                    </span>
                    <span className="pk-small">{response.description}</span>
                  </div>
                </div>
                <pre className="pk-codeblock" style={{ borderRadius: 0 }}>
                  {JSON.stringify(response.example, null, 2)}
                </pre>
              </div>
            ))}
          </div>
        </div>

        {/* Code Examples */}
        {endpoint.examples && endpoint.examples.length > 0 && (
          <div>
            <h4 className="pk-h4" style={{ fontSize: 14, marginBottom: 10 }}>Code Examples</h4>
            <div>
              <div className="pk-codeblock-head">
                {endpoint.examples.map((example, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => setActiveTab(index)}
                    className="name"
                    style={{
                      background: 'none', border: 0, cursor: 'pointer', padding: '2px 8px',
                      color: activeTab === index ? '#fff' : '#948D9B',
                    }}
                  >
                    {example.language}
                  </button>
                ))}
                <div className="actions">
                  <button
                    type="button"
                    onClick={() => copyCode(endpoint.examples[activeTab]?.code || '', `${endpoint.method}-${endpoint.path}-${activeTab}`)}
                    className="pk-copy"
                  >
                    {copiedCode === `${endpoint.method}-${endpoint.path}-${activeTab}` ? (
                      <><CheckCircle size={13} /> Copied</>
                    ) : (
                      <><Copy size={13} /> Copy</>
                    )}
                  </button>
                </div>
              </div>
              <pre className="pk-codeblock">
                {endpoint.examples[activeTab]?.code}
              </pre>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Helper component for error codes
const ErrorCodeCard: React.FC<{
  code: number;
  title: string;
  description: string;
  example: any;
}> = ({ code, title, description, example }) => (
  <div className="pk-card">
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
      <span style={{ color: 'var(--arc-danger)', fontWeight: 700 }}>{code}</span>
      <span style={{ fontWeight: 700, color: 'var(--arc-ink)' }}>{title}</span>
    </div>
    <p className="pk-small" style={{ marginBottom: 10 }}>{description}</p>
    <pre className="pk-codeblock">
      {JSON.stringify(example, null, 2)}
    </pre>
  </div>
);

// Helper function to render method badges
const renderMethod = (method: string) => {
  const cls = {
    'GET': 'pk-method-get',
    'POST': 'pk-method-post',
    'PUT': 'pk-badge-warning',
    'DELETE': 'pk-method-del'
  }[method] || '';
  return <span className={`pk-badge pk-method ${cls}`}>{method}</span>;
};

// Helper function to render auth badges
const renderAuthBadge = (auth: string) => {
  const badges = {
    'jwt': { cls: '', text: 'JWT Required' },
    'api-key': { cls: 'pk-badge-success', text: 'API Key Required' },
    'none': { cls: 'pk-badge-outline', text: 'No Auth' }
  };
  const badge = badges[auth as keyof typeof badges];
  return <span className={`pk-badge ${badge.cls}`}>{badge.text}</span>;
};

export default APIReference;